// bluaIQ — PROJECT-MATERIAL-LIST-1: static demo fixture for the Project Material List
// hidden preview shell.
//
// DEMO DATA ONLY. Every item below is a hand-written literal typed against the
// PROJECT-MATERIAL-LIST-0 canonical contracts (src/shared/projectMaterialList.ts). It
// is NOT produced by any AI system, OCR, PDF parser, or measurement engine, reaches no
// backend, and creates nothing anywhere. Every projectId/sourceId is synthetic —
// deliberately never a real production id (rule 5: no fake/demo data in real projects
// — this fixture only ever renders inside the hidden, dev-gated preview route).

import type {
  ProjectMaterialListItem,
  ProjectMaterialNoSilentDropSummary,
  ProjectMaterialSourceModule,
  ProjectMaterialQuantityStatus,
  ProjectMaterialConfidenceLabel,
  QuoteCoverageInput,
  QuoteComparisonCoverageSummary,
  BudgetDraftItemInput,
  RFQPackageDraftInput,
  ContractDraftInput,
  PaymentScheduleDraftInput,
  GanttProposalInput,
  CalendarProposalInput,
  DashboardReadModelInput,
  AIChatReadContextInput,
} from '../../shared/projectMaterialList'

/** Synthetic demo project id — deliberately NOT a real cuid / production id. */
export const PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID = 'demo-pml-preview-0001'

/**
 * The demo material list: one item per ProjectMaterialSourceModule (7) so every
 * source, quantity status, and confidence label required by PROJECT-MATERIAL-LIST-0
 * is represented at least once.
 */
export const projectMaterialListDemoItems: ProjectMaterialListItem[] = [
  {
    id: 'demo-pml-item-0001',
    projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
    itemName: 'Slab-on-grade concrete (demo)',
    normalizedName: 'concrete_slab_on_grade',
    category: 'Concrete',
    trade: 'Foundation',
    stageGroup: 'foundation',
    description: 'Slab-on-grade concrete pour — demo estimate, current (temporary) Takeoff engine.',
    quantity: 42,
    unit: 'cy',
    quantityStatus: 'draft',
    sourceModule: 'takeoff_current',
    sourceId: 'demo-src-takeoff-current-0001',
    sourceLineId: 'demo-line-0001',
    confidence: 0.55,
    confidenceLabel: 'low',
    needsReview: true,
    reviewReason: 'Current (temporary) Takeoff engine is not the source of truth — quantity unconfirmed.',
    assumptions: [
      { id: 'pml-asm-0001', statement: 'Assumed 4in slab thickness (demo default)', impactIfWrong: 'Concrete volume changes proportionally', mustConfirm: true },
    ],
    blockers: [
      { code: 'demo_takeoff_current_unverified', message: 'Current Takeoff engine output is not authoritative', severity: 'WARNING', isBlocking: false },
    ],
    provenanceRefs: [
      { sourceModule: 'takeoff_current', sourceId: 'demo-src-takeoff-current-0001', sourceLineId: 'demo-line-0001', note: 'Demo — current Takeoff engine' },
    ],
    lastSyncedAt: '2026-07-06T00:00:00.000Z',
    createdAt: '2026-07-06T00:00:00.000Z',
    updatedAt: '2026-07-06T00:00:00.000Z',
  },
  {
    id: 'demo-pml-item-0002',
    projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
    itemName: 'Engineered floor joists (demo)',
    normalizedName: 'floor_joists_engineered',
    category: 'Framing',
    trade: 'Framing',
    stageGroup: 'framing',
    description: 'Engineered I-joists, second floor — demo estimate, future Verified Takeoff.',
    quantity: 68,
    unit: 'ea',
    quantityStatus: 'needs_review',
    sourceModule: 'takeoff_v2',
    sourceId: 'demo-src-takeoff-v2-0001',
    sourceLineId: 'demo-line-0002',
    confidence: 0.86,
    confidenceLabel: 'high',
    needsReview: true,
    reviewReason: 'Span table not yet confirmed by a human reviewer.',
    assumptions: [
      { id: 'pml-asm-0002', statement: 'Assumed 16in on-center spacing (demo default)', impactIfWrong: 'Joist count changes', mustConfirm: true },
    ],
    blockers: [],
    provenanceRefs: [
      { sourceModule: 'takeoff_v2', sourceId: 'demo-src-takeoff-v2-0001', sourceLineId: 'demo-line-0002', note: 'Demo — future Verified Takeoff' },
    ],
    lastSyncedAt: '2026-07-06T00:00:00.000Z',
    createdAt: '2026-07-06T00:00:00.000Z',
    updatedAt: '2026-07-06T00:00:00.000Z',
  },
  {
    id: 'demo-pml-item-0003',
    projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
    itemName: 'Architectural shingles (demo)',
    normalizedName: 'roofing_shingles_architectural',
    category: 'Roofing',
    trade: 'Roofing',
    stageGroup: 'roofing',
    description: 'Architectural shingles, main roof — demo estimate, AI Draft Takeoff.',
    quantity: 38,
    unit: 'sq',
    quantityStatus: 'draft',
    sourceModule: 'ai_draft_takeoff',
    sourceId: 'demo-src-ai-draft-takeoff-0001',
    sourceLineId: 'demo-line-0003',
    confidence: 0.58,
    confidenceLabel: 'low',
    needsReview: true,
    reviewReason: 'AI draft quantity — preliminary only, never a verified quantity until a human confirms.',
    assumptions: [
      { id: 'pml-asm-0003', statement: 'Assumed 12% roofing waste (demo default)', impactIfWrong: 'Material total changes', mustConfirm: true },
    ],
    blockers: [
      { code: 'demo_ai_draft_unverified', message: 'AI draft quantity — human review required before use', severity: 'WARNING', isBlocking: false },
    ],
    provenanceRefs: [
      { sourceModule: 'ai_draft_takeoff', sourceId: 'demo-src-ai-draft-takeoff-0001', sourceLineId: 'demo-line-0003', note: 'Demo — AI Draft Takeoff' },
    ],
    lastSyncedAt: '2026-07-06T00:00:00.000Z',
    createdAt: '2026-07-06T00:00:00.000Z',
    updatedAt: '2026-07-06T00:00:00.000Z',
  },
  {
    id: 'demo-pml-item-0004',
    projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
    itemName: 'Kitchen cabinets, custom order (demo)',
    normalizedName: 'cabinets_kitchen_custom',
    category: 'Finishes',
    trade: 'Cabinetry',
    stageGroup: 'finishes',
    description: 'Custom kitchen cabinet package — demo estimate, manually entered by a human.',
    quantity: 1,
    unit: 'package',
    quantityStatus: 'verified',
    sourceModule: 'manual',
    sourceId: 'demo-src-manual-0001',
    sourceLineId: null,
    confidence: 1,
    confidenceLabel: 'verified',
    needsReview: false,
    reviewReason: null,
    assumptions: [],
    blockers: [],
    provenanceRefs: [
      { sourceModule: 'manual', sourceId: 'demo-src-manual-0001', sourceLineId: null, note: 'Demo — human-entered, confirmed' },
    ],
    lastSyncedAt: null,
    createdAt: '2026-07-06T00:00:00.000Z',
    updatedAt: '2026-07-06T00:00:00.000Z',
  },
  {
    id: 'demo-pml-item-0005',
    projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
    itemName: 'Electrical devices (demo, CSV import)',
    normalizedName: 'electrical_devices_rough_in',
    category: 'Electrical',
    trade: 'Electrical',
    stageGroup: 'electrical',
    description: 'Device rough-in count imported from a supplier CSV — demo estimate.',
    quantity: 96,
    unit: 'ea',
    quantityStatus: 'needs_review',
    sourceModule: 'csv',
    sourceId: 'demo-src-csv-0001',
    sourceLineId: 'demo-line-0005',
    confidence: 0.66,
    confidenceLabel: 'medium',
    needsReview: true,
    reviewReason: 'CSV import column mapping not yet confirmed by a human reviewer.',
    assumptions: [],
    blockers: [
      { code: 'demo_csv_mapping_unconfirmed', message: 'CSV column mapping needs human confirmation', severity: 'WARNING', isBlocking: false },
    ],
    provenanceRefs: [
      { sourceModule: 'csv', sourceId: 'demo-src-csv-0001', sourceLineId: 'demo-line-0005', note: 'Demo — CSV import' },
    ],
    lastSyncedAt: '2026-07-06T00:00:00.000Z',
    createdAt: '2026-07-06T00:00:00.000Z',
    updatedAt: '2026-07-06T00:00:00.000Z',
  },
  {
    id: 'demo-pml-item-0006',
    projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
    itemName: 'Window schedule item (demo, PDF import)',
    normalizedName: 'windows_schedule_item',
    category: 'Windows & Doors',
    trade: 'Envelope',
    stageGroup: 'envelope',
    description: 'Window unit imported from a plan-set PDF schedule — demo estimate.',
    quantity: 14,
    unit: 'ea',
    quantityStatus: 'blocked',
    sourceModule: 'pdf',
    sourceId: 'demo-src-pdf-0001',
    sourceLineId: 'demo-line-0006',
    confidence: 0.4,
    confidenceLabel: 'low',
    needsReview: true,
    reviewReason: 'PDF schedule legend illegible for two window marks — cannot proceed until clarified.',
    assumptions: [
      { id: 'pml-asm-0006', statement: 'Assumed standard egress sizing for unmarked units (demo default)', impactIfWrong: 'Unit count/sizing may be wrong', mustConfirm: true },
    ],
    blockers: [
      { code: 'demo_pdf_schedule_illegible', message: 'Window schedule legend illegible — blocked until confirmed', severity: 'BLOCKING', isBlocking: true },
    ],
    provenanceRefs: [
      { sourceModule: 'pdf', sourceId: 'demo-src-pdf-0001', sourceLineId: 'demo-line-0006', note: 'Demo — PDF import' },
    ],
    lastSyncedAt: '2026-07-06T00:00:00.000Z',
    createdAt: '2026-07-06T00:00:00.000Z',
    updatedAt: '2026-07-06T00:00:00.000Z',
  },
  {
    id: 'demo-pml-item-0007',
    projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
    itemName: 'Framing lumber, existing Material Order (demo)',
    normalizedName: 'lumber_framing_2x4',
    category: 'Framing',
    trade: 'Framing',
    stageGroup: 'framing',
    description: '2x4 studs already captured on an existing Material Order — demo estimate.',
    quantity: 420,
    unit: 'ea',
    quantityStatus: 'verified',
    sourceModule: 'material_order',
    sourceId: 'demo-src-material-order-0001',
    sourceLineId: 'demo-line-0007',
    confidence: 1,
    confidenceLabel: 'verified',
    needsReview: false,
    reviewReason: null,
    assumptions: [],
    blockers: [],
    provenanceRefs: [
      { sourceModule: 'material_order', sourceId: 'demo-src-material-order-0001', sourceLineId: 'demo-line-0007', note: 'Demo — existing Material Order' },
    ],
    lastSyncedAt: '2026-07-06T00:00:00.000Z',
    createdAt: '2026-07-06T00:00:00.000Z',
    updatedAt: '2026-07-06T00:00:00.000Z',
  },
]

/**
 * No-silent-drop proof for the demo fold: every dropped candidate carries an
 * upstreamId + reason (rule 4 — no silent drop).
 */
export const projectMaterialListDemoNoSilentDrop: ProjectMaterialNoSilentDropSummary = {
  totalSourceItems: 9,
  retainedItems: 7,
  droppedItems: [
    { upstreamId: 'demo-pml-drop-0001', reason: 'Illegible handwritten note on the current Takeoff sheet — excluded from demo', sourceModule: 'takeoff_current', sourceLineId: 'demo-line-drop-0001' },
    { upstreamId: 'demo-pml-drop-0002', reason: 'Duplicate line already captured by an existing Material Order', sourceModule: 'material_order', sourceLineId: 'demo-line-drop-0002' },
  ],
  requiresReview: true,
}

/** Convenience export so a caller can assert vocabulary coverage without recomputing it. */
export const PROJECT_MATERIAL_LIST_DEMO_SOURCE_MODULES_USED: ProjectMaterialSourceModule[] =
  projectMaterialListDemoItems.map((i) => i.sourceModule)
export const PROJECT_MATERIAL_LIST_DEMO_QUANTITY_STATUSES_USED: ProjectMaterialQuantityStatus[] =
  projectMaterialListDemoItems.map((i) => i.quantityStatus)
export const PROJECT_MATERIAL_LIST_DEMO_CONFIDENCE_LABELS_USED: ProjectMaterialConfidenceLabel[] =
  projectMaterialListDemoItems.map((i) => i.confidenceLabel)

// ── Downstream read/proposal demo cards — every one read-only/proposal-only, ────────
// approval-gated, no-write. Built directly from the PROJECT-MATERIAL-LIST-0 contracts.

const DEMO_SOURCE_MATERIAL_ITEM_IDS = projectMaterialListDemoItems.map((i) => i.id)

export const projectMaterialListDemoQuoteCoverage: QuoteCoverageInput = {
  projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
  sourceMaterialItemIds: DEMO_SOURCE_MATERIAL_ITEM_IDS,
  requiresHumanApproval: true,
  writesTargetModule: false,
  readOnly: true,
  materialItems: projectMaterialListDemoItems.map((i) => ({
    materialItemId: i.id,
    itemName: i.itemName,
    normalizedName: i.normalizedName,
    quantityStatus: i.quantityStatus,
    needsReview: i.needsReview,
  })),
  sendsRequestQuote: false,
  autoSend: false,
}

export const projectMaterialListDemoQuoteCoverageSummary: QuoteComparisonCoverageSummary = {
  projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
  sourceMaterialItemIds: DEMO_SOURCE_MATERIAL_ITEM_IDS,
  requiresHumanApproval: true,
  writesTargetModule: false,
  totalMaterialItems: projectMaterialListDemoItems.length,
  quotedItems: 3,
  missingItems: [
    { materialItemId: 'demo-pml-item-0005', itemName: 'Electrical devices (demo, CSV import)', reason: 'not_quoted' },
    { materialItemId: 'demo-pml-item-0006', itemName: 'Window schedule item (demo, PDF import)', reason: 'blocked' },
  ],
  singleSupplierItems: 2,
  multiSupplierItems: 1,
  needsReviewItems: projectMaterialListDemoItems.filter((i) => i.needsReview).length,
  readOnly: true,
  generatesRequestQuoteDraftOnly: true,
  autoSend: false,
}

export const projectMaterialListDemoBudgetDraft: BudgetDraftItemInput = {
  projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
  sourceMaterialItemIds: DEMO_SOURCE_MATERIAL_ITEM_IDS,
  requiresHumanApproval: true,
  writesTargetModule: false,
  proposalOnly: true,
  draftBudgetLines: projectMaterialListDemoItems.map((i) => ({
    materialItemId: i.id,
    description: i.itemName,
    quantity: i.quantity,
    unit: i.unit,
    costBasisNote: 'Demo basis only — no committed price',
  })),
  writesBudgetItems: false,
  mutatesFinancialDashboard: false,
}

export const projectMaterialListDemoRfqPackage: RFQPackageDraftInput = {
  projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
  sourceMaterialItemIds: DEMO_SOURCE_MATERIAL_ITEM_IDS,
  requiresHumanApproval: true,
  writesTargetModule: false,
  proposalOnly: true,
  draftOnly: true,
  previewBeforeSend: true,
  autoSend: false,
  matchesSupplierSku: false,
  readyToOrder: false,
  writesMaterialRequestDraft: false,
}

export const projectMaterialListDemoContractDraft: ContractDraftInput = {
  projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
  sourceMaterialItemIds: DEMO_SOURCE_MATERIAL_ITEM_IDS,
  requiresHumanApproval: true,
  writesTargetModule: false,
  proposalOnly: true,
  draftContractInputOnly: true,
  createsContract: false,
  signsContract: false,
  sendsEmail: false,
  createsSignToken: false,
}

export const projectMaterialListDemoPaymentScheduleDraft: PaymentScheduleDraftInput = {
  projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
  sourceMaterialItemIds: DEMO_SOURCE_MATERIAL_ITEM_IDS,
  requiresHumanApproval: true,
  writesTargetModule: false,
  proposalOnly: true,
  draftScheduleInputOnly: true,
  createsPaymentRows: false,
  createsScheduledRows: false,
  createsCancelledRows: false,
  createFlowEnabled: false,
  quickBooksSync: false,
}

export const projectMaterialListDemoGanttProposal: GanttProposalInput = {
  projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
  sourceMaterialItemIds: DEMO_SOURCE_MATERIAL_ITEM_IDS,
  requiresHumanApproval: true,
  writesTargetModule: false,
  proposalOnly: true,
  writesLifecycleTask: false,
  overwritesSchedule: false,
}

export const projectMaterialListDemoCalendarProposal: CalendarProposalInput = {
  projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
  sourceMaterialItemIds: DEMO_SOURCE_MATERIAL_ITEM_IDS,
  requiresHumanApproval: true,
  writesTargetModule: false,
  proposalOnly: true,
  writesCalendarEvent: false,
}

export const projectMaterialListDemoDashboardReadModel: DashboardReadModelInput = {
  projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
  sourceMaterialItemIds: DEMO_SOURCE_MATERIAL_ITEM_IDS,
  requiresHumanApproval: true,
  writesTargetModule: false,
  readOnly: true,
  consumerOnly: true,
  mutatesCommittedFinancials: false,
  mutatesActualsOrPaid: false,
}

export const projectMaterialListDemoAiChatReadContext: AIChatReadContextInput = {
  projectId: PROJECT_MATERIAL_LIST_DEMO_PROJECT_ID,
  sourceMaterialItemIds: DEMO_SOURCE_MATERIAL_ITEM_IDS,
  requiresHumanApproval: true,
  writesTargetModule: false,
  readOnly: true,
  contextOnly: true,
  writes: false,
  createsCommittedObligation: false,
}
